import { IconImage, IconPdf } from "icons";

export interface FileTypeOption {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  description: string;
}

export const FILE_TYPE_OPTIONS: FileTypeOption[] = [
  {
    value: "png",
    icon: IconImage,
    text: "PNG",
    description: "Best for photos",
  },
  {
    value: "jpg",
    icon: IconImage,
    text: "JPG",
    description: "Best for sharing",
  },
  {
    value: "webp",
    icon: IconImage,
    text: "WebP",
    description: "Best for web",
  },
  {
    value: "svg",
    icon: IconPdf,
    text: "SVG",
    description: "Best for printing",
  },
];

// Raster (pixel) formats — as opposed to svg (vector) and mp4 (video). The re-encode, 4K, and
// AI-label logic all key off "is this a raster image". Kept as one list so the checks can't drift.
export const RASTER_FORMATS = ["png", "jpg", "webp"] as const;

export type RasterFormat = typeof RASTER_FORMATS[number];

export const isRasterFormat = (value: string): value is RasterFormat => (RASTER_FORMATS as readonly string[]).includes(value);

export const VIDEO_FILE_TYPE_OPTION: FileTypeOption = {
  value: "mp4",
  icon: IconImage,
  text: "MP4",
  description: "Best for videos",
};

// "Download as 4K". "2k" = download the image as-is (native resolution, labeled "Original");
// "4k" routes non-4K images through the crystal upscaler ("Upscale to 4K"). Only meaningful for
// raster (png/jpg) — SVG is vector (resolution-independent) and video is excluded. The internal
// values stay "2k"/"4k" (useDownloadLogic keys off resolution === "4k"); only the labels changed.
export type DownloadResolution = "2k" | "4k";

// An image whose longest edge is at least this many px is "already 4K" (aspect-agnostic, so a
// 2160×3840 portrait counts). Mirrors the BE constant (services/design.py FOUR_K_LONG_EDGE) so the
// FE-disabled upscale and the BE `already_4k` short-circuit agree. 3840 = UHD long edge.
export const FOUR_K_LONG_EDGE = 3840;

// credit-multiplier key for a 4K upscale. Unresolved until BE adds it → badge renders
// "-" by design (Trap #7); charged only when an upscale actually runs.
export const DOWNLOAD_4K_CREDIT_KEY = "download_4k";

// Watchdog for the 4K upscale: if no WS completion arrives within this window (e.g. a
// dropped socket message), the "Enhancing resolution" popup is dismissed + an error shown
// so it can never hang or stay blocked. A late WS still saves the file regardless, so this
// only bounds how long we wait before showing a give-up error (REF-5392).
//
// 20 min (was 5 min) — REF-5737: the crystal-upscaler worst case (large image + FAL cold
// start) exceeds 5 min, so the old value false-timed-out succeeding jobs at exactly 300s,
// showing "Couldn't create the 4K image" while the BE was still working (Trap #9: hand-rolled
// timeouts pick the wrong constant). 20 min matches the canonical useSocketFallback
// maxPollingDuration (docs/rules/websocket-jobs.md).
export const DOWNLOAD_4K_TIMEOUT_MS = 20 * 60 * 1000;

export interface ResolutionOption {
  value: DownloadResolution;
  // i18n key under common: namespace. "2k" → Original, "4k" → Upscale to 4K.
  labelKey: string;
}

export const RESOLUTION_OPTIONS: ResolutionOption[] = [
  { value: "2k", labelKey: "common:download_menu.resolution_original" },
  { value: "4k", labelKey: "common:download_menu.resolution_upscale_4k" },
];
